/* jp-nav.js — desktop mega-menu hover-intent + viewport clamp.
   Keyboard opens the panel via CSS :focus-within; JS mirrors aria-expanded
   for screen readers and adds the mouse open (120ms) / close (240ms) delay so the panel
   doesn't flicker on pass-through, and nudges wide panels inside the viewport
   via the --jp-mega-shift custom property. Esc closes any hover-opened panel.
   No jQuery / no frameworks. Guarded against double execution. */
(() => {
  if (window.__jpNavInit) return;
  window.__jpNavInit = true;

  const OPEN_DELAY = 120, CLOSE_DELAY = 240, MARGIN = 16;
  /* Touch/no-hover tablets (iPad / Surface in tablet mode) can't hover the parent link,
     so hover-open is skipped for them and replaced with the tap-to-open path below. */
  const noHover = () => window.matchMedia && window.matchMedia('(hover: none)').matches;

  const items = () => Array.prototype.slice
    .call(document.querySelectorAll('.jp-nav__item'))
    .filter((it) => it.querySelector('.jp-mega'));

  const syncItem = (it) => {
    const link = it.querySelector('.jp-nav__link');
    if (link) link.setAttribute('aria-expanded',
      (!it.classList.contains('is-escape-closed') && (it.classList.contains('is-open') || it.contains(document.activeElement))) ? 'true' : 'false');
  };
  const closeItem = (it, suppressFocusedPanel = false) => {
    it.__jpNavBound?.cancelTimers();
    it.classList.remove('is-open');
    /* Removing .is-open is not enough while the disclosure still owns focus:
       CSS :focus-within would keep the panel visible. Outside clicks and
       explicit toggles therefore suppress that one focused panel until focus
       genuinely re-enters it. */
    if (suppressFocusedPanel && it.contains(document.activeElement)) it.classList.add('is-escape-closed');
    syncItem(it);
  };
  const closeAll = (except, suppressFocusedPanel = false) => items().forEach((it) => {
    if (it !== except) closeItem(it, suppressFocusedPanel);
  });

  /* mutual exclusion: opening a mega collapses the persona popover */
  const closePersona = () => document.querySelectorAll('[data-jp-persona].is-open').forEach((root) => {
    const menu = root.querySelector('.jp-persona__menu');
    const pill = root.querySelector('.jp-persona-pill');
    if (menu) menu.hidden = true;
    if (pill) pill.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
  });

  const clamp = (mega) => {
    if (!mega) return;
    mega.style.setProperty('--jp-mega-shift', '0px');
    const initialRect = mega.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const availableHeight = Math.max(96, viewportHeight - Math.max(MARGIN, initialRect.top) - MARGIN);
    mega.style.setProperty('--jp-mega-max-height', Math.floor(availableHeight) + 'px');
    const r = mega.getBoundingClientRect();
    let shift = 0;
    if (r.right > window.innerWidth - MARGIN) shift = (window.innerWidth - MARGIN) - r.right;
    else if (r.left < MARGIN) shift = MARGIN - r.left;
    if (shift) mega.style.setProperty('--jp-mega-shift', Math.round(shift) + 'px');
  };

  const bind = (item) => {
    if (item.__jpNavBound) return;
    const listeners = new AbortController();
    const { signal } = listeners;
    const mega = item.querySelector('.jp-mega');
    let openT, closeT, focusT;
    const cancelTimers = () => {
      window.clearTimeout(openT);
      window.clearTimeout(closeT);
      window.clearTimeout(focusT);
    };
    item.__jpNavBound = {
      cancelTimers,
      destroy() {
        cancelTimers();
        listeners.abort();
        closeItem(item, true);
        delete item.__jpNavBound;
      },
    };
    item.addEventListener('mouseenter', () => {
      if (noHover()) return;
      cancelTimers();
      openT = window.setTimeout(() => {
        if (!item.isConnected) return;
        closeAll(item, true); closePersona(); clamp(mega);
        item.classList.remove('is-escape-closed');
        item.classList.add('is-open'); syncItem(item);
      }, OPEN_DELAY);
    }, { signal });
    item.addEventListener('mouseleave', () => {
      if (noHover()) return;
      cancelTimers();
      closeT = window.setTimeout(() => closeItem(item), CLOSE_DELAY);
    }, { signal });
    /* keyboard: CSS :focus-within opens the panel — mirror that into aria-expanded */
    item.addEventListener('focusin', (e) => {
      if (!item.contains(e.relatedTarget)) item.classList.remove('is-escape-closed');
      clamp(mega); syncItem(item);
    }, { signal });
    item.addEventListener('focusout', () => {
      window.clearTimeout(focusT);
      focusT = window.setTimeout(() => {
        if (!item.isConnected) return;
        if (!item.contains(document.activeElement)) item.classList.remove('is-escape-closed');
        syncItem(item);
      }, 0);
    }, { signal });
    /* Disclosure buttons toggle on every input type. Collection-parent anchors
       keep their ecommerce contract: on no-hover devices the first tap opens
       their children and the second follows the collection destination. */
    const link = item.querySelector('.jp-nav__link');
    if (link) link.addEventListener('click', (e) => {
      if (link.tagName === 'BUTTON') {
        e.preventDefault();
        /* Pointer focus arrives before click, so :focus-within alone must not
           make the first pointer click look like a close. Keyboard-generated
           clicks have detail=0 and may toggle that focus-open state directly. */
        const expanded = e.detail === 0
          ? link.getAttribute('aria-expanded') === 'true'
          : item.classList.contains('is-open');
        if (expanded) {
          item.classList.add('is-escape-closed');
          closeItem(item);
          link.setAttribute('aria-expanded', 'false');
          return;
        }
        closeAll(item, true); closePersona(); clamp(mega);
        item.classList.remove('is-escape-closed');
        item.classList.add('is-open'); syncItem(item);
        return;
      }
      if (!noHover()) return;                          // pointer devices: collection link navigates
      if (item.classList.contains('is-open')) return;  // second tap: let the collection link navigate
      e.preventDefault();
      closeAll(item, true); closePersona(); clamp(mega);
      item.classList.remove('is-escape-closed');
      item.classList.add('is-open'); syncItem(item);
    }, { signal });
  };

  const boot = () => items().forEach(bind);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const activeItem = document.activeElement?.closest?.('.jp-nav__item');
    if (activeItem?.querySelector('.jp-mega')) {
      e.preventDefault();
      activeItem.classList.add('is-escape-closed');
      closeItem(activeItem);
      const link = activeItem.querySelector('.jp-nav__link');
      if (link) { link.setAttribute('aria-expanded', 'false'); link.focus(); }
    }
    closeAll(activeItem || null);
  });
  /* Any outside click closes the active disclosure. This also handles a
     focus-open desktop panel when the shopper clicks non-focusable whitespace. */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.jp-nav__item')) closeAll(null, true);
  });
  let resizeFrame = 0;
  const reclampVisible = () => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => items().forEach((item) => {
      if (item.classList.contains('is-open') || item.contains(document.activeElement)) clamp(item.querySelector('.jp-mega'));
    }));
  };
  window.addEventListener('resize', reclampVisible, { passive: true });
  window.visualViewport?.addEventListener('resize', reclampVisible, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    if (scope?.matches?.('.jp-nav__item')) scope.__jpNavBound?.destroy();
    scope?.querySelectorAll?.('.jp-nav__item').forEach((item) => item.__jpNavBound?.destroy());
  });
})();
